import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar, AlertCircle, Clock, Plus } from 'lucide-react'

interface MaintenanceSchedule {
  id: string
  vehicle_id: string
  vehicle_name: string
  current_odometer: number
  service_type: string
  title: string
  description: string | null
  interval_type: string
  interval_miles: number | null
  interval_days: number | null
  next_due_date: number | null
  next_due_odometer: number | null
  priority: string
  is_recurring: number
  status: 'overdue' | 'due_soon' | 'scheduled'
  days_until_due: number | null
}

export function UpcomingMaintenance() {
  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-schedules'],
    queryFn: async () => {
      const res = await fetch('/api/maintenance/schedules')
      if (!res.ok) throw new Error('Failed to fetch maintenance schedules')
      return res.json() as Promise<{ schedules: MaintenanceSchedule[] }>
    },
    refetchInterval: 60000,
  })

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      oil_change: 'Oil Change',
      tire_rotation: 'Tire Rotation',
      brake_service: 'Brake Service',
      inspection: 'Inspection',
      repair: 'Repair',
      other: 'Other'
    }
    return labels[type] || type
  }

  const getStatusBadge = (schedule: MaintenanceSchedule) => {
    if (schedule.status === 'overdue') {
      return <Badge variant="destructive">Overdue</Badge>
    }
    if (schedule.status === 'due_soon') {
      return <Badge className="bg-orange-500">Due Soon</Badge>
    }
    return <Badge variant="outline">Scheduled</Badge>
  }

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      critical: 'text-red-600 dark:text-red-400',
      high: 'text-orange-600 dark:text-orange-400',
      medium: 'text-yellow-600 dark:text-yellow-400',
      low: 'text-muted-foreground'
    }
    return colors[priority] || colors.low
  }

  // Sort: overdue first, then due soon, then by priority
  const sortedSchedules = [...(data?.schedules || [])].sort((a, b) => {
    const statusOrder = { overdue: 0, due_soon: 1, scheduled: 2 }
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }

    if (a.status !== b.status) {
      return statusOrder[a.status] - statusOrder[b.status]
    }

    return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder]
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Maintenance
          </CardTitle>
          <Button size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            Schedule Maintenance
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading maintenance schedules...
          </div>
        ) : !data?.schedules || data.schedules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No upcoming maintenance scheduled</p>
            <Button size="sm" variant="outline" className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Add Schedule
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedSchedules.map((schedule) => {
              const milesRemaining = schedule.next_due_odometer && schedule.current_odometer
                ? schedule.next_due_odometer - schedule.current_odometer
                : null

              return (
                <Card key={schedule.id} className={`hover:shadow-md transition-shadow ${schedule.status === 'overdue' ? 'border-destructive' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Service info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 ${getPriorityColor(schedule.priority)}`}>
                            {schedule.status === 'overdue' ? (
                              <AlertCircle className="h-5 w-5" />
                            ) : (
                              <Clock className="h-5 w-5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{schedule.title}</h3>
                              {getStatusBadge(schedule)}
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">
                              {schedule.vehicle_name} • {getServiceTypeLabel(schedule.service_type)}
                            </p>
                            {schedule.description && (
                              <p className="text-xs text-muted-foreground">{schedule.description}</p>
                            )}
                          </div>
                        </div>

                        {/* Due info */}
                        <div className="flex flex-wrap gap-4 text-sm ml-8">
                          {schedule.next_due_date && (
                            <div>
                              <span className="text-muted-foreground">Due: </span>
                              <span className={schedule.status === 'overdue' ? 'text-destructive font-medium' : ''}>
                                {schedule.days_until_due !== null ? (
                                  schedule.days_until_due < 0 ? (
                                    `${Math.abs(schedule.days_until_due)} days overdue`
                                  ) : (
                                    `in ${schedule.days_until_due} days`
                                  )
                                ) : (
                                  new Date(schedule.next_due_date * 1000).toLocaleDateString()
                                )}
                              </span>
                            </div>
                          )}
                          {milesRemaining !== null && (
                            <div>
                              <span className="text-muted-foreground">Miles: </span>
                              <span className={milesRemaining <= 0 ? 'text-destructive font-medium' : ''}>
                                {milesRemaining <= 0 ? 'Overdue' : `${milesRemaining.toLocaleString()} mi remaining`}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Interval info */}
                        {schedule.is_recurring === 1 && (
                          <div className="text-xs text-muted-foreground ml-8">
                            <span className="font-medium">Recurring:</span>{' '}
                            {schedule.interval_miles && `Every ${schedule.interval_miles.toLocaleString()} miles`}
                            {schedule.interval_miles && schedule.interval_days && ' or '}
                            {schedule.interval_days && `Every ${schedule.interval_days} days`}
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex flex-col gap-2">
                        <Button size="sm" variant="outline">
                          Complete
                        </Button>
                        <Button size="sm" variant="ghost">
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
